import { FunctionComponent, memo, useEffect, useRef } from "react";
import { faBolt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import { IProps } from "./interaction-message.types";
import { determineClassName } from "./interaction-message.utilities";
import InteractionMessageContent from "./interaction-message-content";
import { InteractionMessageType, InteractionType } from "@edgetier/watchtower-types";
import "./interaction-message.scss";

/**
 * An interaction message. The message is slightly larger if it's an email and it will indicate if a message has been
 * sent by a bot.
 * @param props.message           The message object to display.
 * @param props.interactionType   The type of interaction that the message belongs to.
 * @param props.writingIssues     An array of writing issues associated with the message.
 */
const InteractionMessage: FunctionComponent<IProps> = ({
    message,
    interactionType,
    isMessageHighlighted,
    writingIssues,
    senderDetails,
    activeTranslation,
    toggleTranslation,
}) => {
    const rowRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (rowRef.current) {
            rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [isMessageHighlighted]);

    return (
        <div className="interaction-message__container" ref={isMessageHighlighted ? rowRef : undefined}>
            <div
                className={classnames(determineClassName(message.direction, "interaction-message"), {
                    "interaction-message__email-message": interactionType === InteractionType.Email,
                    "interaction-message--system": message.messageType === InteractionMessageType.System,
                    "interaction-message--highlighted": isMessageHighlighted,
                })}
            >
                {message.isBot && message.messageType !== InteractionMessageType.System && (
                    <div className="interaction-message__bot-indicator">
                        <FontAwesomeIcon icon={faBolt} title="bot-message" />
                    </div>
                )}
                <InteractionMessageContent
                    interactionType={interactionType}
                    message={message}
                    writingIssues={writingIssues}
                    senderDetails={senderDetails}
                    activeTranslation={activeTranslation}
                    toggleTranslation={toggleTranslation}
                />
            </div>
        </div>
    );
};

export default memo(InteractionMessage);
